import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { HeroSection } from '../components/sections/HeroSection';
import { ProductCard } from '../components/product/ProductCard';
import { RecipeBundle } from '../components/product/RecipeBundle';
import { mockProducts } from '../data/mockProducts';
import { CheckCircle2, Truck, Shield } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  }
};
export function HomePage() {
  const featuredProducts = mockProducts.slice(0, 8);
  const jollofBundle = {
    title: 'Jollof Rice Essentials',
    description: 'Everything you need for authentic Jollof rice',
    products: [mockProducts[2], mockProducts[3], mockProducts[5]],
    totalPrice: 23.97,
    savings: 3.0
  };
  return <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-8" data-id="element-328">
      <TopNav data-id="element-329" />
      <EBTWICBanner data-id="element-330" />
      <HeroSection data-id="element-331" />

      {/* Trust Indicators */}
      <motion.section initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-100px'
    }} variants={containerVariants} className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-332">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-333">
          <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-334">
            <div className="flex items-start gap-4" data-id="element-335">
              <div className="bg-[#ff6b35]/10 p-3 rounded-xl" data-id="element-336">
                <CheckCircle2 className="w-6 h-6 text-[#ff6b35]" data-id="element-337" />
              </div>
              <div data-id="element-338">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-339">
                  WIC & EBT Accepted
                </h3>
                <p className="text-sm text-gray-600" data-id="element-340">
                  Use your benefits on eligible items
                </p>
              </div>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-341">
            <div className="flex items-start gap-4" data-id="element-342">
              <div className="bg-[#ff6b35]/10 p-3 rounded-xl" data-id="element-343">
                <Truck className="w-6 h-6 text-[#ff6b35]" data-id="element-344" />
              </div>
              <div data-id="element-345">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-346">
                  Free Delivery $35+
                </h3>
                <p className="text-sm text-gray-600" data-id="element-347">
                  Same-day delivery available
                </p>
              </div>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-348">
            <div className="flex items-start gap-4" data-id="element-349">
              <div className="bg-[#1e3a8a]/10 p-3 rounded-xl" data-id="element-350">
                <Shield className="w-6 h-6 text-[#1e3a8a]" data-id="element-351" />
              </div>
              <div data-id="element-352">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-353">
                  Quality Guaranteed
                </h3>
                <p className="text-sm text-gray-600" data-id="element-354">
                  Fresh, authentic products
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-id="element-355">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-8" data-id="element-356">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-357">
            Featured Products
          </h2>
          <p className="text-gray-600" data-id="element-358">
            Authentic ingredients for your traditional recipes
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={containerVariants} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-359">
          {featuredProducts.map(product => <motion.div key={product.id} variants={itemVariants} data-id="element-360">
              <ProductCard product={product} data-id="element-361" />
            </motion.div>)}
        </motion.div>
      </section>

      {/* Recipe Bundle */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-id="element-362">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-8" data-id="element-363">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-364">
            Recipe Bundles
          </h2>
          <p className="text-gray-600" data-id="element-365">
            Save time and money with our curated bundles
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-366">
          <RecipeBundle {...jollofBundle} data-id="element-367" />
        </motion.div>
      </section>

      {/* WIC Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-id="element-368">
        <div className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-3xl p-8 md:p-12 text-white" data-id="element-369">
          <div className="max-w-2xl" data-id="element-370">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-371">
              Shop with WIC & EBT
            </h2>
            <p className="text-lg text-white/90 mb-6" data-id="element-372">
              We're proud to accept WIC and EBT/SNAP benefits. Look for the
              badges on eligible products throughout our store.
            </p>
            <a href="/wic-ebt-guide" className="inline-flex items-center gap-2 bg-white text-[#1e3a8a] font-bold px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-373">
              Learn More
            </a>
          </div>
        </div>
      </motion.section>

      <BottomNav data-id="element-374" />
    </div>;
}